<script setup>
import { computed, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import { messageDayTimestamp, messageStamp } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';

const { contentAttributes, status, sender, createdAt, messageType } =
  useMessageContext();

const showDetails = ref(false);

const hasError = computed(() => {
  return status.value === MESSAGE_STATUS.FAILED;
});

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);

const fromEmail = computed(() => {
  return contentAttributes.value?.email?.from ?? [];
});

const toEmail = computed(() => {
  const { toEmails, email } = contentAttributes.value;
  return email?.to ?? toEmails ?? [];
});

const ccEmail = computed(() => {
  return (
    contentAttributes.value?.ccEmails ??
    contentAttributes.value?.email?.cc ??
    []
  );
});

const senderName = computed(() => {
  const fromEmailAddress = fromEmail.value[0] ?? '';
  const senderEmail = sender.value?.email ?? '';

  if (!fromEmailAddress && !senderEmail) return null;

  // if the sender of the conversation and the sender of this particular
  // email are the same, only then we return the sender name
  if (fromEmailAddress === senderEmail) {
    return sender.value.name;
  }

  return null;
});

const bccEmail = computed(() => {
  return (
    contentAttributes.value?.bccEmails ??
    contentAttributes.value?.email?.bcc ??
    []
  );
});

const subject = computed(() => {
  return contentAttributes.value?.email?.subject ?? '';
});

const showMeta = computed(() => {
  return (
    fromEmail.value[0] ||
    toEmail.value.length ||
    ccEmail.value.length ||
    bccEmail.value.length ||
    subject.value
  );
});

// Nome principal do remetente: nome conhecido, senão o próprio endereço.
const displayName = computed(
  () => senderName.value || fromEmail.value[0] || sender.value?.name || ''
);

const recipientCount = computed(
  () => toEmail.value.length + ccEmail.value.length + bccEmail.value.length
);

// Resumo de uma linha, como nos clientes de e-mail: primeiro destinatário e "+N".
const recipientSummary = computed(() => {
  const [first] = [...toEmail.value, ...ccEmail.value, ...bccEmail.value];
  if (!first) return '';
  const others = recipientCount.value - 1;
  return others > 0 ? `${first} +${others}` : first;
});

const readableTime = computed(() =>
  createdAt.value ? messageDayTimestamp(createdAt.value) : ''
);
const fullTime = computed(() =>
  createdAt.value ? messageStamp(createdAt.value, 'dd/MM/yyyy, HH:mm') : ''
);
</script>

<template>
  <section
    v-show="showMeta"
    class="min-w-0 break-words"
    :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-11'"
    data-test-id="email-meta"
  >
    <template v-if="showMeta">
      <h3
        class="mb-3 text-[15px] font-semibold leading-snug"
        :class="[
          hasError ? 'text-n-ruby-11' : 'text-n-slate-12',
          { 'italic text-n-slate-10': !subject },
        ]"
        data-test-id="email-subject"
      >
        {{ subject || $t('EMAIL_HEADER.NO_SUBJECT') }}
      </h3>
      <div class="flex min-w-0 items-start gap-3">
        <Avatar
          :name="displayName || '?'"
          :src="senderName ? sender?.thumbnail || '' : ''"
          :size="36"
          rounded-full
          class="shrink-0"
        />
        <div class="min-w-0 flex-1">
          <div class="flex min-w-0 items-baseline justify-between gap-3">
            <p class="mb-0 min-w-0 truncate text-sm leading-5">
              <span
                class="font-semibold"
                :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-12'"
                data-test-id="email-sender"
              >
                {{ displayName }}
              </span>
              <span
                v-if="senderName && fromEmail[0]"
                class="ms-1 text-xs text-n-slate-10"
              >
                &lt;{{ fromEmail[0] }}&gt;
              </span>
            </p>
            <time
              v-if="readableTime"
              class="shrink-0 text-xs tabular-nums text-n-slate-10"
              :title="fullTime"
            >
              {{ readableTime }}
            </time>
          </div>
          <div class="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs">
            <span
              v-if="isOutgoing"
              class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-brand/10 px-1.5 py-px text-[11px] font-medium text-n-brand"
            >
              <Icon icon="i-hugeicons-mail-send-01" class="size-3" />
              {{ $t('EMAIL_HEADER.SENT') }}
            </span>
            <button
              v-if="recipientCount"
              type="button"
              class="inline-flex min-w-0 items-center gap-1 rounded text-start hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :aria-expanded="showDetails"
              :title="
                $t(
                  showDetails
                    ? 'EMAIL_HEADER.HIDE_DETAILS'
                    : 'EMAIL_HEADER.SHOW_DETAILS'
                )
              "
              data-test-id="email-recipients-toggle"
              @click="showDetails = !showDetails"
            >
              <span class="truncate">
                {{ $t('EMAIL_HEADER.TO').toLowerCase() }}
                {{ recipientSummary }}
              </span>
              <Icon
                :icon="
                  showDetails
                    ? 'i-hugeicons-arrow-up-01'
                    : 'i-hugeicons-arrow-down-01'
                "
                class="size-3 shrink-0"
              />
            </button>
          </div>
          <dl
            v-if="showDetails"
            class="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs"
            data-test-id="email-recipients-details"
          >
            <template v-if="fromEmail[0]">
              <dt class="text-n-slate-10">{{ $t('EMAIL_HEADER.FROM') }}</dt>
              <dd class="mb-0 text-n-slate-12">{{ fromEmail[0] }}</dd>
            </template>
            <template v-if="toEmail.length">
              <dt class="text-n-slate-10">{{ $t('EMAIL_HEADER.TO') }}</dt>
              <dd class="mb-0 text-n-slate-12">{{ toEmail.join(', ') }}</dd>
            </template>
            <template v-if="ccEmail.length">
              <dt class="text-n-slate-10">{{ $t('EMAIL_HEADER.CC') }}</dt>
              <dd class="mb-0 text-n-slate-12">{{ ccEmail.join(', ') }}</dd>
            </template>
            <template v-if="bccEmail.length">
              <dt class="text-n-slate-10">{{ $t('EMAIL_HEADER.BCC') }}</dt>
              <dd class="mb-0 text-n-slate-12">{{ bccEmail.join(', ') }}</dd>
            </template>
            <template v-if="fullTime">
              <dt class="text-n-slate-10">{{ $t('EMAIL_HEADER.DATE') }}</dt>
              <dd class="mb-0 text-n-slate-12">{{ fullTime }}</dd>
            </template>
          </dl>
        </div>
      </div>
    </template>
  </section>
</template>
